import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNotify, useRecordContext } from 'react-admin';

import { apiFetch } from '../../api/client';

interface Section {
  id: string;
  section_key: string;
  label: string;
  display_order: number;
  is_enabled_default: boolean;
  is_required: boolean;
  config_schema: Record<string, unknown>;
}

const KNOWN_SECTION_KEYS = ['hero', 'about', 'products', 'gallery', 'map', 'qr', 'feedback', 'leads', 'custom_html'];

function extractPreviewVariables(html: string): string[] {
  const matches = html.match(/\{(\w+)\}/g) ?? [];
  return Array.from(new Set(matches.map((m) => m.slice(1, -1))));
}

export function TemplateSectionsPanel() {
  const record = useRecordContext();
  const notify = useNotify();
  const templateId = record?.id as string | undefined;

  const [sections, setSections] = useState<Section[]>([]);
  const [busy, setBusy] = useState(false);

  const [newSectionKey, setNewSectionKey] = useState('custom_html');
  const [newLabel, setNewLabel] = useState('');
  const [newHtml, setNewHtml] = useState('');

  const [editingHtml, setEditingHtml] = useState<Record<string, string>>({});

  const load = async () => {
    if (!templateId) return;
    try {
      const data = await apiFetch<{ sections: Section[] }>(`/admin/templates/${templateId}`);
      setSections(data.sections);
    } catch {
      setSections([]);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  if (!templateId) {
    return (
      <Box sx={{ width: 380, ml: 2 }}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Save the template first, then come back here to add sections
            (including the "HTML page" custom_html section).
          </Typography>
        </Paper>
      </Box>
    );
  }

  const handleAddSection = async () => {
    setBusy(true);
    try {
      const configSchema = newSectionKey === 'custom_html' ? { html: newHtml } : {};
      await apiFetch(`/admin/templates/${templateId}/sections`, {
        method: 'POST',
        body: {
          section_key: newSectionKey,
          label: newLabel || newSectionKey,
          display_order: sections.length,
          config_schema: configSchema,
        },
      });
      notify('Section added', { type: 'success' });
      setNewLabel('');
      setNewHtml('');
      await load();
    } catch (err) {
      notify((err as Error).message || 'Failed to add section', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handleSaveHtml = async (section: Section) => {
    setBusy(true);
    try {
      const html = editingHtml[section.id] ?? (section.config_schema.html as string) ?? '';
      await apiFetch(`/admin/templates/${templateId}/sections/${section.id}`, {
        method: 'PATCH',
        body: { config_schema: { ...section.config_schema, html } },
      });
      notify('Section HTML updated', { type: 'success' });
      await load();
    } catch (err) {
      notify((err as Error).message || 'Failed to update — variables may be locked', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handleToggleEnabled = async (section: Section) => {
    setBusy(true);
    try {
      await apiFetch(`/admin/templates/${templateId}/sections/${section.id}`, {
        method: 'PATCH',
        body: { is_enabled_default: !section.is_enabled_default },
      });
      await load();
    } catch (err) {
      notify((err as Error).message || 'Failed to update section', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ width: 380, ml: 2 }}>
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Sections
          </Typography>
          <Stack spacing={2} divider={<Divider />}>
            {sections.map((s) => (
              <Box key={s.id}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" fontWeight={600}>
                    {s.label} <Chip size="small" label={s.section_key} sx={{ ml: 1 }} />
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={s.is_enabled_default}
                        onChange={() => handleToggleEnabled(s)}
                        disabled={busy}
                      />
                    }
                    label="Enabled"
                  />
                </Stack>

                {s.section_key === 'custom_html' && (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    {Array.isArray(s.config_schema.variables) && (s.config_schema.variables as string[]).length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Locked variables:{' '}
                        {(s.config_schema.variables as string[]).map((v) => `{${v}}`).join(', ')}
                      </Typography>
                    )}
                    <TextField
                      multiline
                      minRows={3}
                      size="small"
                      label="HTML template"
                      defaultValue={(s.config_schema.html as string) ?? ''}
                      onChange={(e) => setEditingHtml({ ...editingHtml, [s.id]: e.target.value })}
                    />
                    <Button size="small" variant="outlined" disabled={busy} onClick={() => handleSaveHtml(s)}>
                      Save HTML
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      You can restyle the HTML, but the same {'{variable}'}{' '}
                      placeholders must stay — adding/removing/renaming variables
                      is blocked once this section is created.
                    </Typography>
                  </Stack>
                )}
              </Box>
            ))}
            {sections.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                No sections yet.
              </Typography>
            )}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Add section
          </Typography>
          <Stack spacing={1.5}>
            <Select size="small" value={newSectionKey} onChange={(e) => setNewSectionKey(e.target.value)}>
              {KNOWN_SECTION_KEYS.map((k) => (
                <MenuItem key={k} value={k}>
                  {k}
                </MenuItem>
              ))}
            </Select>
            <TextField
              size="small"
              label="Label"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
            />
            {newSectionKey === 'custom_html' && (
              <>
                <TextField
                  multiline
                  minRows={3}
                  size="small"
                  label="HTML template — use {variable} placeholders"
                  placeholder={'<div class="promo">\n  <h2>{headline}</h2>\n  <p>{price}</p>\n</div>'}
                  value={newHtml}
                  onChange={(e) => setNewHtml(e.target.value)}
                />
                {extractPreviewVariables(newHtml).length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Will lock in variables:{' '}
                    {extractPreviewVariables(newHtml).map((v) => `{${v}}`).join(', ')}
                  </Typography>
                )}
              </>
            )}
            <Button variant="contained" size="small" disabled={busy} onClick={handleAddSection}>
              Add section
            </Button>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
}
